// Phase 45.5.3 (CANVAS-14 / D-1) — <CanvasLocationField /> canvas-local
// multi-location checkbox grid + ⭐ primary, ported from v1
// ListingDetailsStep.tsx:226-323 (+ auto-primary effect 83-98).
//
// Replaces the canvas's single primary_location_id text input. Writes
// location_ids[] (the array of checked location ids) and primary_location_id
// (the ⭐-marked id). No backend change — buildBoatPayload.ts already persists
// both keys (RESEARCH Open Q #1), so D-1 is a pure UI swap.
//
// NO PROVENANCE (RESEARCH Pitfall 4): location is never AI-filled, so this field
// deliberately omits the provenance tint-wrapper and provenance chip — v1's
// location card has none either. (No useProvenance import here.)
//
// D-5 / D-15: imports v1 useLocations READ-ONLY; canvas-local; no wizard-folder edits (V1 era).
//
// THREAT MODEL (45.5.3-01 <threat_model>): location names render as React text
// children (auto-escaped). No name reaches dangerouslySetInnerHTML; setValue on
// the ⭐ click writes through react-hook-form only — no window.* side channel.

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { MapPin, Star, Loader2 } from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import { Checkbox } from '@marine/ui/checkbox';
import { useLocations } from '@/modules/inventory/hooks/useLocations';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';

export function CanvasLocationField() {
  const { control, watch, setValue } = useFormContext<WizardFormData>();

  // NOTE: useLocations returns the array DIRECTLY (no `.items` envelope) —
  // see the interfaces block in 45.5.3-01-PLAN.md.
  const { data, isLoading } = useLocations();
  // Only surface ACTIVE locations as options. This mirrors the all-active
  // pre-select seed in LiveFillCanvas (buildAllActiveLocationDefaults, which
  // filters `is_active !== false`), so every card shown here is checked by
  // default — an Inactive location must not appear as an unchecked card. The
  // backend /locations index returns ALL locations (Location::all(), ignores
  // any is_active param), so the filter has to happen client-side.
  const locations = (data ?? []).filter(
    (location) => location.is_active !== false,
  );

  const selectedLocationIds = watch('location_ids') ?? [];
  const primaryLocationId = watch('primary_location_id');

  // Auto-primary (ported verbatim from ListingDetailsStep.tsx:83-98):
  //  - exactly one location selected → make it primary
  //  - primary was unchecked while others remain → clear primary
  //  - all locations unchecked → clear primary
  useEffect(() => {
    if (selectedLocationIds.length === 1) {
      setValue('primary_location_id', selectedLocationIds[0], {
        shouldValidate: true,
      });
    } else if (
      selectedLocationIds.length > 0 &&
      primaryLocationId &&
      !selectedLocationIds.includes(primaryLocationId)
    ) {
      setValue('primary_location_id', '', { shouldValidate: true });
    } else if (selectedLocationIds.length === 0 && primaryLocationId) {
      setValue('primary_location_id', '', { shouldValidate: true });
    }
  }, [selectedLocationIds, primaryLocationId, setValue]);

  return (
    <FormField
      control={control}
      name="location_ids"
      render={() => (
        <FormItem>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <FormLabel className="text-xs">
              Locations <span className="text-destructive">*</span>
            </FormLabel>
          </div>
          {isLoading ? (
            <div className="flex items-center h-8.5 px-3">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs text-muted-foreground">
                Loading locations...
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
              {Array.isArray(locations) &&
                locations.map((location: { id: string; name: string }) => (
                  <FormField
                    key={location.id}
                    control={control}
                    name="location_ids"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2.5 space-y-0 rounded-md border border-border px-3 py-2.5 hover:bg-accent/50 transition-colors">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(location.id)}
                            onCheckedChange={(checked) => {
                              const current = field.value ?? [];
                              if (checked) {
                                field.onChange([...current, location.id]);
                              } else {
                                field.onChange(
                                  current.filter(
                                    (id: string) => id !== location.id,
                                  ),
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-2sm font-normal cursor-pointer flex-1">
                          {location.name}
                        </FormLabel>
                        {/* ⭐ primary toggle — only for selected rows. */}
                        {selectedLocationIds.includes(location.id) && (
                          <button
                            type="button"
                            className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
                            aria-label={
                              primaryLocationId === location.id
                                ? `${location.name} is the primary location`
                                : `Set ${location.name} as primary location`
                            }
                            title={
                              primaryLocationId === location.id
                                ? 'Primary location'
                                : 'Set as primary location'
                            }
                            onClick={() =>
                              setValue('primary_location_id', location.id, {
                                shouldValidate: true,
                              })
                            }
                          >
                            <Star
                              className={`h-4 w-4 ${
                                primaryLocationId === location.id
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          </button>
                        )}
                      </FormItem>
                    )}
                  />
                ))}
            </div>
          )}
          {selectedLocationIds.length > 0 && primaryLocationId && (
            <p className="text-xs text-muted-foreground">
              Primary:{' '}
              {locations.find(
                (l: { id: string; name: string }) => l.id === primaryLocationId,
              )?.name ?? 'Unknown'}
            </p>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
